import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { ApiError } from '@/api/client.types';

import type {
  AssignmentScore,
  GradebookAssignment,
  GradebookStudent,
  QuestionScore,
  StudentGradesState,
} from './gradebookTypes';
import { buildQuestionMetaKey } from './gradebookTypes';
import type { SaveGradeEditParams } from './gradebookService';
import { useKuduGradebookService } from './gradebookService';
import { studentGradesQueryKeyPrefix } from './useStudentGrades';

function createOptimisticQuestionScore(
  question:
    | GradebookAssignment['sections'][number]['homeworkQuestions'][number]
    | GradebookAssignment['sections'][number]['preLectureQuestions'][number],
  currentQuestion: QuestionScore | null,
  editedQuestionScores: Record<string, number>,
  sectionStatsId: string,
): QuestionScore {
  const scoreKey = buildQuestionMetaKey(question);
  const fallbackScore = currentQuestion?.score ?? 0;
  const edited = scoreKey in editedQuestionScores;

  return {
    // A saved edit is instructor-adjusted, as the server will mark it.
    adjusted: edited || (currentQuestion?.adjusted ?? false),
    attempts: currentQuestion?.attempts ?? 0,
    id: currentQuestion?.id ?? question.id,
    index: question.index,
    kind: question.kind,
    label: question.label,
    maxScore: question.maxScore,
    needsGrading: currentQuestion?.needsGrading ?? false,
    score: editedQuestionScores[scoreKey] ?? fallbackScore,
    sectionId: question.sectionId,
    sectionStatsId,
  };
}

export function applyEditedScoresToAssignment(
  assignment: GradebookAssignment,
  assignmentScore: AssignmentScore | null,
  editedQuestionScores: Record<string, number>,
): AssignmentScore {
  const sectionScores = assignment.sections.map((section) => {
    const currentSectionScore =
      assignmentScore?.sectionScores.find(
        (sectionScore) => sectionScore.id === section.id,
      ) ?? null;
    const sectionStatsId = currentSectionScore?.sectionStatsId ?? section.id;
    const questionScores = section.homeworkQuestions.map((question) =>
      createOptimisticQuestionScore(
        question,
        currentSectionScore?.questionScores.find(
          (candidate) =>
            candidate.index === question.index &&
            candidate.kind === question.kind &&
            candidate.sectionId === question.sectionId,
        ) ?? null,
        editedQuestionScores,
        sectionStatsId,
      ),
    );
    const preLectureQuestionScores = section.preLectureQuestions.map((question) =>
      createOptimisticQuestionScore(
        question,
        currentSectionScore?.preLectureQuestionScores.find(
          (candidate) =>
            candidate.index === question.index &&
            candidate.kind === question.kind &&
            candidate.sectionId === question.sectionId,
        ) ?? null,
        editedQuestionScores,
        sectionStatsId,
      ),
    );

    return {
      homeworkScore:
        questionScores.length > 0
          ? questionScores.reduce(
              (total, question) => total + (question.score ?? 0),
              0,
            )
          : null,
      id: section.id,
      preLectureQuestionScores,
      preLectureScore:
        preLectureQuestionScores.length > 0
          ? preLectureQuestionScores.reduce(
              (total, question) => total + (question.score ?? 0),
              0,
            )
          : null,
      questionScores,
      sectionStatsId,
    };
  });

  return {
    componentId: assignment.componentId,
    componentTitle: assignment.title,
    componentUid: assignment.componentUid,
    homeworkPossible: assignment.homeworkMaxScore,
    homeworkScore:
      assignment.homeworkMaxScore > 0
        ? sectionScores.reduce(
            (total, section) => total + (section.homeworkScore ?? 0),
            0,
          )
        : null,
    // Participation is server-authored (Kai Coach) and never edited here.
    participationPossible: 0,
    participationScore: null,
    preLecturePossible: assignment.preLectureMaxScore,
    preLectureQuestionScores: sectionScores.flatMap(
      (section) => section.preLectureQuestionScores,
    ),
    preLectureScore:
      assignment.preLectureMaxScore > 0
        ? sectionScores.reduce(
            (total, section) => total + (section.preLectureScore ?? 0),
            0,
          )
        : null,
    questionScores: sectionScores.flatMap((section) => section.questionScores),
    sectionScores,
  };
}

export function applyEditedScoresToStudent(
  student: GradebookStudent,
  assignment: GradebookAssignment,
  editedQuestionScores: Record<string, number>,
): GradebookStudent {
  const nextAssignmentScore = applyEditedScoresToAssignment(
    assignment,
    student.assignmentScores[assignment.componentUid] ?? null,
    editedQuestionScores,
  );
  const nextAssignmentScores = {
    ...student.assignmentScores,
    [assignment.componentUid]: nextAssignmentScore,
  };

  return {
    ...student,
    assignmentScores: nextAssignmentScores,
    hasLoadedGrades: true,
    totalHomeworkScore: Object.values(nextAssignmentScores).reduce(
      (total, score) => total + (score.homeworkScore ?? 0),
      0,
    ),
    totalPreLectureScore: Object.values(nextAssignmentScores).reduce(
      (total, score) => total + (score.preLectureScore ?? 0),
      0,
    ),
  };
}

export function useSaveGrade(courseId: string | undefined) {
  const queryClient = useQueryClient();
  const service = useKuduGradebookService();

  return useMutation<
    void,
    ApiError,
    SaveGradeEditParams,
    { previousStates: Array<[readonly unknown[], StudentGradesState | undefined]> }
  >({
    mutationFn: async (params) => {
      if (!service || !courseId) {
        const error = new Error('Unable to save grade edits right now.') as ApiError;
        error.kind = 'data';
        throw error;
      }

      await service.saveGradeEdit(params);
    },
    onMutate: async (params) => {
      if (!courseId) {
        return { previousStates: [] as Array<[readonly unknown[], StudentGradesState | undefined]> };
      }

      const previousStates = queryClient.getQueriesData<StudentGradesState>({
        queryKey: studentGradesQueryKeyPrefix(courseId),
      });

      queryClient.setQueriesData<StudentGradesState>(
        { queryKey: studentGradesQueryKeyPrefix(courseId) },
        (currentState) => {
          if (!currentState) {
            return currentState;
          }

          return {
            ...currentState,
            students: currentState.students.map((student) =>
              student.id === params.student.id
                ? applyEditedScoresToStudent(
                    student,
                    params.assignment,
                    params.editedQuestionScores,
                  )
                : student,
            ),
          };
        },
      );

      return { previousStates };
    },
    onError: (_error, _variables, context) => {
      context?.previousStates.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });
    },
    // Reload what is stored, whether the save worked or not, so an edit that
    // did not persist can never look saved (the optimistic update above would
    // otherwise stand until a page reload).
    onSettled: () => {
      if (courseId) {
        void queryClient.invalidateQueries({
          queryKey: studentGradesQueryKeyPrefix(courseId),
        });
      }
    },
  });
}
